// AdminLeasingAgents — PMR-side management for leasing-agent magic links.
// Create/rotate/deactivate agents, edit per-unit access codes (entry code,
// alarm, lockbox, wifi, parking), and watch a live feed of every agent's
// reports, showings, and messages across the whole portfolio.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Copy, ArrowClockwise, Trash, LockKey, Users, Wrench, ClipboardText,
  ChatText, PaperPlaneTilt, X, House,
} from "@phosphor-icons/react";
import { AdminShell as Layout } from "@/components/Layout";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PropertyEnrichCard from "@/components/PropertyEnrichCard";
import RentEstimateChip from "@/components/RentEstimateChip";

export default function AdminLeasingAgents() {
  const nav = useNavigate();
  const [tab, setTab] = useState("units");
  const [agents, setAgents] = useState([]);
  const [feed, setFeed] = useState({ reports: [], showings: [], messages: [] });
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openAgent, setOpenAgent] = useState(null);
  const [openUnit, setOpenUnit] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [a, f, u] = await Promise.all([
        api.get("/leasing/agents"),
        api.get("/leasing/feed"),
        api.get("/leasing/vacant-units"),
      ]);
      setAgents(a.data.agents || []);
      setFeed(f.data || {});
      setUnits(u.data.units || []);
    } catch (e) { toast.error("Could not load leasing data"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => ({
    agents: agents.length,
    active: agents.filter(a => a.active !== false).length,
    reports: feed.reports?.length || 0,
    showings: feed.showings?.length || 0,
    vacant: units.length,
  }), [agents, feed, units]);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="admin-leasing">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Leasing operations</div>
            <h1 className="text-2xl font-black mt-1">Leasing Agent Portal</h1>
            <p className="text-sm text-slate-500 mt-1">
              Give leasing agents a private link with entry codes + report buttons. Owners spend less time on the phone; you catch every showing.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="text-xs font-black bg-cyan-600 text-white rounded-lg px-3 py-2 flex items-center gap-1.5 hover:bg-cyan-700"
            data-testid="admin-leasing-new-agent-btn">
            <Plus weight="bold" size={14}/> New agent link
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <Stat n={stats.agents} label="Agents" color="text-slate-900" onClick={() => setTab("agents")}/>
          <Stat n={stats.active} label="Active" color="text-emerald-600" onClick={() => setTab("agents")}/>
          <Stat n={stats.reports} label="Reports (feed)" color="text-orange-600" onClick={() => setTab("feed")}/>
          <Stat n={stats.showings} label="Showings logged" color="text-cyan-600" onClick={() => setTab("feed")}/>
          <Stat n={stats.vacant} label="Vacant units" color="text-fuchsia-600" onClick={() => setTab("units")}/>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
          {[["agents", "Agents", Users], ["feed", "Activity feed", ClipboardText], ["units", "Unit codes", House]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1 border-b-2 -mb-px ${tab === id ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`admin-leasing-tab-${id}`}>
              <Icon size={12}/> {label}
            </button>
          ))}
        </div>

        {loading && <div className="text-sm text-slate-400">Loading…</div>}

        {tab === "agents" && !loading && (
          <div className="space-y-3">
            {!agents.length && (
              <div className="bg-slate-50 rounded-2xl p-10 text-center text-slate-500">
                <div className="font-black text-slate-700 text-base mb-1">No leasing agents yet</div>
                <div className="text-sm">Click <strong>"New agent link"</strong> — you'll get a private URL to text or email.</div>
                {stats.vacant > 0 && (
                  <button onClick={() => setTab("units")}
                    className="mt-4 text-xs font-black text-cyan-600 hover:text-cyan-700 underline"
                    data-testid="admin-leasing-empty-jump-units">
                    You have {stats.vacant} vacant unit{stats.vacant === 1 ? "" : "s"} ready to show — view them →
                  </button>
                )}
              </div>
            )}
            {agents.map(a => (
              <AgentCard key={a.id} a={a} onOpen={() => setOpenAgent(a)} onReload={load}/>
            ))}
          </div>
        )}

        {tab === "feed" && !loading && <ActivityFeed feed={feed}/>}

        {tab === "units" && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {!units.length && (
              <div className="col-span-full bg-slate-50 rounded-2xl p-10 text-center text-slate-500">
                No vacant units. Mark a unit vacant via Properties → Unit detail.
              </div>
            )}
            {units.map(u => (
              <UnitCodesCard key={u.unit_key} u={u} onOpen={() => setOpenUnit(u)}/>
            ))}
          </div>
        )}
      </div>

      {showCreate && <NewAgentModal onClose={() => setShowCreate(false)} onCreated={load}/>}
      {openAgent && <AgentDrawer agent={openAgent} onClose={() => { setOpenAgent(null); load(); }}/>}
      {openUnit && <UnitCodesModal u={openUnit} onClose={() => setOpenUnit(null)} onSaved={load}/>}
    </Layout>
  );
}

function Stat({ n, label, color = "text-slate-900", onClick }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap onClick={onClick} className={`bg-white rounded-xl p-3 shadow-sm text-left ${onClick ? "hover:shadow-md hover:ring-2 hover:ring-cyan-200 transition cursor-pointer" : ""}`}>
      <div className={`text-2xl font-black ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </Wrap>
  );
}

function AgentCard({ a, onOpen, onReload }) {
  const copyLink = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(a.url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const rotate = async (e) => {
    e.stopPropagation();
    if (!confirm("Rotate this agent's link? The old URL will stop working.")) return;
    try {
      await api.post(`/leasing/agents/${a.id}/rotate-token`);
      toast.success("Token rotated");
      onReload && onReload();
    } catch { toast.error("Failed"); }
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!confirm(`Delete ${a.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/leasing/agents/${a.id}`);
      toast.success("Deleted");
      onReload && onReload();
    } catch { toast.error("Failed"); }
  };
  return (
    <button onClick={onOpen} className="w-full text-left bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition"
      data-testid={`admin-leasing-agent-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-black text-sm shrink-0">
          {(a.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-black text-slate-900">{a.name}</div>
            {a.active !== false ? (
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Active</span>
            ) : (
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Inactive</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {a.email || a.phone || "—"}
            {a.landlord_id ? " · scoped to owner" : " · full portfolio"}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
            <span>{a.reports_count} report{a.reports_count === 1 ? "" : "s"}</span>
            <span>· {a.showings_count} showing{a.showings_count === 1 ? "" : "s"}</span>
            <span>· {a.messages_count} msg</span>
            {a.last_seen_at && <span>· last seen {a.last_seen_at.slice(5, 16).replace("T", " ")}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={copyLink} title="Copy link" className="h-8 w-8 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid={`admin-leasing-copy-${a.id}`}>
            <Copy size={13}/>
          </button>
          <button onClick={rotate} title="Rotate token" className="h-8 w-8 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid={`admin-leasing-rotate-${a.id}`}>
            <ArrowClockwise size={13}/>
          </button>
          <button onClick={remove} title="Delete" className="h-8 w-8 rounded bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`admin-leasing-delete-${a.id}`}>
            <Trash size={13}/>
          </button>
        </div>
      </div>
    </button>
  );
}

function ActivityFeed({ feed }) {
  const combined = useMemo(() => {
    const rows = [];
    (feed.reports || []).forEach(r => rows.push({ ...r, _kind: "report", _when: r.at }));
    (feed.showings || []).forEach(s => rows.push({ ...s, _kind: "showing", _when: s.checked_in_at }));
    (feed.messages || []).forEach(m => rows.push({ ...m, _kind: "message", _when: m.at }));
    return rows.sort((a, b) => (b._when || "").localeCompare(a._when || ""));
  }, [feed]);

  const photoUrl = (path) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const tok = encodeURIComponent(localStorage.getItem("token") || "");
    return `${base}/api/leasing/report-photo?path=${encodeURIComponent(path)}&auth=${tok}`;
  };

  if (!combined.length) return (
    <div className="bg-slate-50 rounded-2xl p-10 text-center text-slate-500" data-testid="admin-leasing-feed-empty">
      Nothing yet. Once agents check in or file reports, they'll show up here in real time.
    </div>
  );

  return (
    <div className="space-y-2" data-testid="admin-leasing-feed">
      {combined.map((row, i) => (
        <div key={i} className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            {row._kind === "report" ? <Wrench size={16} className="text-orange-500"/>
              : row._kind === "showing" ? <ClipboardText size={16} className="text-cyan-500"/>
              : <ChatText size={16} className="text-slate-500"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              <span className="font-bold">{row.agent_name}</span>
              {" · "}
              {row._kind === "report" ? <><span className="text-orange-700 font-bold">reported</span> {row.title} <span className="text-slate-500">at {row.address}{row.unit ? ` #${row.unit}` : ""}</span></>
                : row._kind === "showing" ? <><span className="text-cyan-700 font-bold">checked in</span> {row.prospect_name ? `with ${row.prospect_name}` : ""}</>
                : <span className="text-slate-700">said: "{row.text}"</span>}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">{row._when?.slice(0, 16).replace("T", " ")}</div>
            {row._kind === "report" && row.detail && <div className="text-xs text-slate-600 mt-1 italic">{row.detail}</div>}
            {row._kind === "report" && row.photos?.length ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {row.photos.map((p, pi) => (
                  <a key={pi} href={photoUrl(p)} target="_blank" rel="noreferrer"
                    className="w-14 h-14 rounded overflow-hidden bg-slate-100 block"
                    data-testid={`admin-leasing-report-photo-${i}-${pi}`}>
                    <img src={photoUrl(p)} alt="" className="w-full h-full object-cover"/>
                  </a>
                ))}
              </div>
            ) : null}
            {row._kind === "report" && row.wo_id && (
              <a href={`/admin/jobs/${row.wo_id}`} className="text-[11px] font-bold text-cyan-600 hover:text-cyan-700 mt-1 inline-block">Open work order →</a>
            )}
          </div>
          <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
            row._kind === "report" ? "bg-orange-100 text-orange-700"
            : row._kind === "showing" ? "bg-cyan-100 text-cyan-700"
            : "bg-slate-100 text-slate-600"}`}>{row._kind}</span>
        </div>
      ))}
    </div>
  );
}

function UnitCodesCard({ u, onOpen }) {
  return (
    <button onClick={onOpen} className="text-left bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition"
      data-testid={`admin-leasing-unit-${u.unit_key}`}>
      <div className="font-black text-sm truncate">{u.address}</div>
      {u.unit && <div className="text-xs text-slate-500">Unit {u.unit}</div>}
      <div className="mt-2 flex flex-wrap gap-1 text-[10px] font-black uppercase tracking-widest">
        {u.entry_code ? <span className="bg-cyan-50 text-cyan-700 rounded px-1.5 py-0.5">Code ✓</span> : <span className="bg-slate-100 text-slate-400 rounded px-1.5 py-0.5">No code</span>}
        {u.alarm_code ? <span className="bg-orange-50 text-orange-700 rounded px-1.5 py-0.5">Alarm ✓</span> : null}
        {u.lockbox_location ? <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">Lockbox ✓</span> : null}
        {u.wifi_ssid ? <span className="bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5">WiFi ✓</span> : null}
      </div>
      {u.listing_rent_cents ? (
        <div className="mt-1">
          <RentEstimateChip
            rentCents={u.listing_rent_cents}
            source={u.listing_rent_source}
            rationale={u.listing_rent_rationale}
            rangeLowCents={u.listing_rent_range_low_cents}
            rangeHighCents={u.listing_rent_range_high_cents}
            confidence={u.listing_rent_confidence}
            neighborhood={u.listing_neighborhood}
            updatedAt={u.listing_rent_updated_at}
            size="sm"
          />
        </div>
      ) : null}
    </button>
  );
}

function NewAgentModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function submit() {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/leasing/agents", { name, email, phone, notes });
      setResult(r.data.agent);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  async function copy() {
    if (!result?.url) return;
    try { await navigator.clipboard.writeText(result.url); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="admin-leasing-new-modal">
        {!result ? (
          <>
            <h3 className="text-lg font-black mb-1">New leasing agent</h3>
            <p className="text-xs text-slate-500 mb-4">They'll get a private link with entry codes for every vacant unit + a report button that opens WOs directly.</p>
            <div className="space-y-3">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Name"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-leasing-name"/>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-leasing-email"/>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone (optional)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-leasing-phone"/>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-leasing-notes"/>
              <div className="flex gap-2 mt-2">
                <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
                <button onClick={submit} disabled={busy} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="admin-leasing-create-btn">
                  {busy ? "Creating…" : "Create link"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="admin-leasing-new-result">
            <div className="text-lg font-black">{result.name}'s link is ready</div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Shareable URL</div>
              <div className="text-xs font-mono text-slate-700 break-all">{result.url}</div>
              <button onClick={copy} className="mt-2 bg-slate-900 text-white text-xs font-black px-3 py-2 rounded-lg flex items-center gap-1" data-testid="admin-leasing-new-copy">
                <Copy size={12}/> Copy link
              </button>
            </div>
            <button onClick={onClose} className="w-full bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function AgentDrawer({ agent, onClose }) {
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/leasing/agents/${agent.id}`);
      setDetail(r.data);
    } catch { toast.error("Could not load"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agent.id]);

  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/leasing/agents/${agent.id}/message`, { text: reply });
      setReply("");
      await load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-leasing-drawer">
        <div className="p-5 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black tracking-widest text-cyan-500">Leasing agent</div>
            <div className="font-black text-lg">{agent.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{agent.email || agent.phone || "—"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
        </div>
        {!detail && <div className="p-5 text-sm text-slate-400">Loading…</div>}
        {detail && (
          <div className="p-5 space-y-5">
            <section>
              <h4 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Reports ({detail.reports?.length || 0})</h4>
              {detail.reports?.length ? detail.reports.slice().reverse().map((r, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 mb-2 text-sm" data-testid={`admin-leasing-report-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className={`shrink-0 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${r.priority === "urgent" ? "bg-red-100 text-red-700" : r.priority === "high" ? "bg-orange-100 text-orange-700" : "bg-cyan-50 text-cyan-700"}`}>{r.priority}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.address}{r.unit ? ` · Unit ${r.unit}` : ""} · {r.at?.slice(0, 16).replace("T", " ")}</div>
                      {r.detail && <div className="text-xs text-slate-600 mt-1 italic">{r.detail}</div>}
                      {r.photos?.length ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {r.photos.map((p, pi) => {
                            const base = process.env.REACT_APP_BACKEND_URL;
                            const tok = encodeURIComponent(localStorage.getItem("token") || "");
                            const src = `${base}/api/leasing/report-photo?path=${encodeURIComponent(p)}&auth=${tok}`;
                            return (
                              <a key={pi} href={src} target="_blank" rel="noreferrer" className="w-16 h-16 rounded overflow-hidden bg-slate-100 block">
                                <img src={src} alt="" className="w-full h-full object-cover"/>
                              </a>
                            );
                          })}
                        </div>
                      ) : null}
                      {r.wo_id && <a href={`/admin/jobs/${r.wo_id}`} className="text-[11px] font-bold text-cyan-600 hover:text-cyan-700 mt-1 inline-block">Open work order →</a>}
                    </div>
                  </div>
                </div>
              )) : <div className="text-xs text-slate-400 italic">No reports.</div>}
            </section>

            <section>
              <h4 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Showings ({detail.showings?.length || 0})</h4>
              {detail.showings?.length ? detail.showings.slice().reverse().map((s, i) => (
                <div key={i} className="text-xs bg-slate-50 rounded p-2 mb-1" data-testid={`admin-leasing-showing-${i}`}>
                  <span className="font-bold">{s.prospect_name || "—"}</span>
                  <span className="text-slate-500"> · checked in {s.checked_in_at?.slice(5, 16).replace("T", " ")}</span>
                  {s.checked_out_at && <span className="text-slate-500"> · out {s.checked_out_at.slice(11, 16)}</span>}
                </div>
              )) : <div className="text-xs text-slate-400 italic">No showings logged.</div>}
            </section>

            <section>
              <h4 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Messages</h4>
              <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
                {(detail.messages || []).map((m, i) => (
                  <div key={i} className={`text-sm rounded-lg p-2.5 ${m.from === "admin" ? "bg-cyan-50 ml-8 text-cyan-900" : "bg-slate-100 mr-8"}`} data-testid={`admin-leasing-msg-${i}`}>
                    <div className="text-[10px] uppercase font-black tracking-wider opacity-60">{m.from}{m.author ? ` · ${m.author}` : ""}</div>
                    <div>{m.text}</div>
                  </div>
                ))}
                {!(detail.messages || []).length && <div className="text-xs text-slate-400 italic">No messages.</div>}
              </div>
              <div className="flex gap-2">
                <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Reply…"
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="admin-leasing-drawer-reply"/>
                <button disabled={busy || !reply.trim()} onClick={send} className="bg-slate-900 text-white rounded-lg px-4 disabled:opacity-40" data-testid="admin-leasing-drawer-send">
                  <PaperPlaneTilt weight="fill" size={16}/>
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function UnitCodesModal({ u, onClose, onSaved }) {
  const [f, setF] = useState({
    entry_code: u.entry_code || "",
    alarm_code: u.alarm_code || "",
    alarm_disarm_code: u.alarm_disarm_code || "",
    alarm_notes: u.alarm_notes || "",
    lockbox_location: u.lockbox_location || "",
    wifi_ssid: u.wifi_ssid || "",
    wifi_password: u.wifi_password || "",
    parking_notes: u.parking_notes || "",
    listing_rent_cents: u.listing_rent_cents || "",
    listing_bedrooms: u.listing_bedrooms || "",
    listing_bathrooms: u.listing_bathrooms || "",
    listing_sqft: u.listing_sqft || "",
    listing_notes: u.listing_notes || "",
  });
  const [photos, setPhotos] = useState(u.listing_photos || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useState(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  async function uploadPhoto(files) {
    if (!files?.length) return;
    setUploading(true);
    let added = 0;
    for (const file of Array.from(files).slice(0, 12)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post(`/leasing/unit/${u.unit_key}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos(prev => [...prev, r.data.path]);
        added += 1;
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? `Failed: ${file.name}`)); }
    }
    if (added) toast.success(`${added} photo${added === 1 ? "" : "s"} uploaded`);
    setUploading(false);
    onSaved && onSaved();
  }
  async function removePhoto(path) {
    if (!confirm("Remove this photo?")) return;
    try {
      await api.delete(`/leasing/unit/${u.unit_key}/photo`, { params: { path } });
      setPhotos(prev => prev.filter(p => p !== path));
      onSaved && onSaved();
    } catch { toast.error("Failed"); }
  }

  async function refetchAndReset() {
    try {
      const r = await api.get("/leasing/vacant-units");
      const fresh = (r.data.units || []).find(x => x.unit_key === u.unit_key);
      if (fresh) {
        setF(prev => ({
          ...prev,
          listing_rent_cents: fresh.listing_rent_cents || prev.listing_rent_cents,
          listing_bedrooms: fresh.listing_bedrooms || prev.listing_bedrooms,
          listing_bathrooms: fresh.listing_bathrooms || prev.listing_bathrooms,
          listing_sqft: fresh.listing_sqft || prev.listing_sqft,
        }));
      }
    } catch { /* silent */ }
    onSaved && onSaved();
  }

  async function save() {
    setBusy(true);
    try {
      const payload = { ...f };
      ["listing_rent_cents", "listing_bedrooms", "listing_bathrooms", "listing_sqft"].forEach(k => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      Object.keys(payload).forEach(k => payload[k] === null && delete payload[k]);
      await api.patch(`/leasing/unit/${u.unit_key}/codes`, payload);
      toast.success("Saved");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  const F = ({ k, label, placeholder = "" }) => (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">{label}</div>
      <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} placeholder={placeholder}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`admin-leasing-unit-${k}`}/>
    </label>
  );
  const photoUrl = (path) => `${BASE}/api/leasing/unit/${u.unit_key}/photo?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-leasing-unit-modal">
        <h3 className="text-lg font-black mb-1">Unit access codes</h3>
        <div className="text-xs text-slate-500 mb-4">{u.address}{u.unit ? ` · Unit ${u.unit}` : ""}</div>

        <div className="mb-4">
          <PropertyEnrichCard address={u.address} unit={u.unit}
            extraNotes={f.listing_notes} onApplied={refetchAndReset} dense/>
        </div>

        {/* Listing photos */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Listing photos <span className="text-slate-400 font-normal normal-case">({photos.length}/12)</span></div>
            <label className="cursor-pointer text-[10px] uppercase font-black tracking-widest bg-slate-900 hover:bg-black text-white px-3 py-1.5 rounded" data-testid="admin-leasing-photo-upload-label">
              {uploading ? "Uploading…" : "+ Add photos"}
              <input type="file" accept="image/*" multiple className="hidden"
                disabled={uploading || photos.length >= 12}
                onChange={e => uploadPhoto(e.target.files)}
                data-testid="admin-leasing-photo-upload-input"/>
            </label>
          </div>
          {photos.length ? (
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={p} className="relative aspect-square rounded overflow-hidden bg-slate-100 group" data-testid={`admin-leasing-photo-${i}`}>
                  <img src={photoUrl(p)} alt="" className="w-full h-full object-cover"/>
                  <button onClick={() => removePhoto(p)}
                    className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-black opacity-0 group-hover:opacity-100 transition"
                    data-testid={`admin-leasing-photo-remove-${i}`}>×</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic border-2 border-dashed border-slate-200 rounded p-4 text-center">
              No photos yet. Add up to 12 — leasing agents see them in their portal.
            </div>
          )}
        </div>

        <div className="space-y-3">
          <F k="entry_code" label="Entry code" placeholder="e.g. 1234#"/>
          <F k="alarm_code" label="Alarm code"/>
          <F k="alarm_disarm_code" label="Alarm disarm code (if different)"/>
          <F k="alarm_notes" label="Alarm notes" placeholder="Where is the panel? Delay window?"/>
          <F k="lockbox_location" label="Lockbox location" placeholder="Left of front door"/>
          <F k="wifi_ssid" label="WiFi SSID"/>
          <F k="wifi_password" label="WiFi password"/>
          <F k="parking_notes" label="Parking notes"/>
          <F k="listing_rent_cents" label="Rent (cents) — e.g. 145000 for $1450"/>
          <div className="grid grid-cols-3 gap-2">
            <F k="listing_bedrooms" label="Bd"/>
            <F k="listing_bathrooms" label="Ba"/>
            <F k="listing_sqft" label="Sqft"/>
          </div>
          <F k="listing_notes" label="Listing notes"/>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="admin-leasing-unit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
